/** Tenant → Users: the organization's own directory, from GET /users. Owner
 *  reps get certification reports, not the staff list — the API's 403 renders
 *  as an explanation, not an error. */
import { useEffect, useState } from 'react';
import { ApiError, request } from '../api';
import { ROLE_LABEL, timeAgo } from '../adminApi';

interface DirectoryUser {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  lastActiveAt: string | null;
  isSelf: boolean;
}

const ROLE_SCOPE: Record<string, string> = {
  compliance_lead: 'Everything, all properties',
  regional_manager: 'Assigned properties',
  site_coordinator: 'Assigned properties',
  owner_rep: 'Reports and certification status',
};

export function Users() {
  const [users, setUsers] = useState<DirectoryUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    request<{ users: DirectoryUser[] }>('/users')
      .then((d) => setUsers(d.users))
      .catch((err) =>
        setError(
          err instanceof ApiError && err.status === 403
            ? 'Your role does not have access to the user directory.'
            : 'Could not load users.',
        ),
      );
  }, []);

  const STATUS_TAG: Record<string, [string, string]> = {
    active: ['ok', 'Active'],
    invited: ['warn', 'Invited'],
    disabled: ['grey', 'Disabled'],
  };

  const list = users ?? [];
  const active = list.filter((u) => u.status === 'active').length;
  const pending = list.filter((u) => u.status === 'invited').length;

  return (
    <>
      <div className="page__head">
        <div>
          <h1>Users</h1>
          <p className="sub">{users ? `${active} active${pending ? ` · ${pending} pending` : ''}` : '—'}</p>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <h2>Your team</h2>
        </div>
        <div className="table-scroll">
          <table className="tbl">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Access</th>
                <th>Last active</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {error && (
                <tr>
                  <td colSpan={6} className="dim" style={{ padding: '22px 13px' }}>
                    {error}
                  </td>
                </tr>
              )}
              {users && list.length === 0 && (
                <tr>
                  <td colSpan={6} className="dim" style={{ padding: '22px 13px' }}>
                    No users.
                  </td>
                </tr>
              )}
              {list.map((u) => {
                const tag = STATUS_TAG[u.status] ?? ['grey', u.status];
                return (
                  <tr key={u.id}>
                    <td className="strong">
                      {u.name} {u.isSelf && <span className="dim">(you)</span>}
                    </td>
                    <td className="dim">{u.email}</td>
                    <td>{ROLE_LABEL[u.role] ?? u.role}</td>
                    <td className="dim">{ROLE_SCOPE[u.role] ?? '—'}</td>
                    <td className="dim">{timeAgo(u.lastActiveAt)}</td>
                    <td>
                      <span className={`tag tag--${tag[0]}`}>{tag[1]}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="panel__foot">User management — adding people, roles, property access — is handled by Tenpoint during onboarding.</div>
      </div>
    </>
  );
}
